import React, { useState, useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { Stack, Button, Shelf, Text } from '@chaser/components';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError';
import { MappingTypeMoneyDecimalSeparatorSelect } from '../Mapping/Type/Money';
import { MappingTypeDateFormatSelect } from '../Mapping/Type/Date';
import ImportService from '../../../../services/import-service';
import styles from './GeneralConfiguration.module.scss';
import { toast } from '../../../../components/ToastContainer';
import { isEqual } from 'lodash';
import { GeneralConfigurationFieldSeparatorSelect } from './FieldSeparatorSelect';

const GeneralConfiguration = ({ shortCode, configuration, onCompleted }) => {
  const [error, setError] = useState(null);
  const initialValues = useMemo(
    () => ({
      fieldSeparator: configuration?.fieldSeparator || 'COMMA',
      dateFormat: configuration?.dateFormat || 'yyyy-MM-dd',
      moneyDecimalSeparator: configuration?.moneyDecimalSeparator || 'DOT',
    }),
    [configuration],
  );
  const [lastSavedValues, setLastSavedValues] = useState(initialValues);

  const saveConfiguration = useCallback(
    async values => {
      try {
        await ImportService.saveCSVMappingConfiguration(shortCode, {
          configuration: values,
        });
        toast.success('Configuration saved successfully.');
        setLastSavedValues(values);
        onCompleted();
      } catch (err) {
        const { code } = err;
        if (code === 'CSV_MAPPING_NOT_MATCHING_SPECIFICATION') {
          setError({
            message: `Please correct following problems and try again:`,
            list: err.meta.errors,
          });
          toast.error('Failed to save configuration. Please correct listed issues and try again.');
        } else {
          toast.error(
            'Unexpected error occured. Please try again. If problem persists, contact support.',
          );
        }
      }
    },
    [shortCode, onCompleted],
  );

  return (
    <Formik onSubmit={saveConfiguration} initialValues={initialValues}>
      {({ isSubmitting, values }) => (
        <Form>
          <Stack gap="medium">
            <Stack gap="medium" className={styles.settings}>
              <LabeledFieldWithError
                containerStyles={{
                  flex: '1 1 100%',
                }}
                label="Field separator"
                name="fieldSeparator"
                labelColor="Dark Neutral 1"
                labelFontSize="medium"
                as={GeneralConfigurationFieldSeparatorSelect}
              />

              <LabeledFieldWithError
                containerStyles={{
                  flex: '1 1 100%',
                }}
                label="Date format"
                name="dateFormat"
                labelColor="Dark Neutral 1"
                labelFontSize="medium"
                as={MappingTypeDateFormatSelect}
                hideDefault
              />

              <LabeledFieldWithError
                containerStyles={{
                  flex: '1 1 100%',
                }}
                label="Decimal separator"
                name="moneyDecimalSeparator"
                labelColor="Dark Neutral 1"
                labelFontSize="medium"
                as={MappingTypeMoneyDecimalSeparatorSelect}
                hideDefault
              />
            </Stack>

            <Shelf justifyContent="space-between" alignItems="flex-start">
              <Text color="Red">
                {error && (
                  <>
                    <div>
                      <strong>{error.message}</strong>
                    </div>
                    {error.list && (
                      <ul>
                        {error.list.map(err => (
                          <li>{err}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </Text>

              <Button
                as="button"
                type="submit"
                loading={isSubmitting}
                success={isEqual(values, lastSavedValues)}
                successMessage="Saved"
              >
                Save
              </Button>
            </Shelf>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default GeneralConfiguration;
